import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, Clock, TrendingUp, TrendingDown, Filter, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Overtime() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  
  // Filters
  const [filterUser, setFilterUser] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  // Form
  const [formData, setFormData] = useState({
    user_id: '',
    hours: '',
    reason: '',
    date: format(new Date(), 'yyyy-MM-dd')
  });

  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;

  // Fetch team members - depends on user being authenticated
  useEffect(() => {
    if (!user) return;
    
    const fetchTeam = async () => {
      try {
        const res = await axios.get(`${API}/api/users`);
        // Include all active users (including directors)
        const activeUsers = res.data.filter(u => u.is_active !== false);
        setTeam(activeUsers);
      } catch (error) {
        console.error('Error fetching team:', error);
      }
    };
    fetchTeam();
  }, [user]);

  // Fetch overtime entries
  const fetchEntries = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filterUser && filterUser !== 'all') {
        params.append('user_id', filterUser);
      }
      if (filterStartDate) {
        params.append('start_date', filterStartDate);
      }
      if (filterEndDate) {
        params.append('end_date', filterEndDate);
      }
      
      const res = await axios.get(`${API}/api/overtime?${params.toString()}`);
      setEntries(res.data);
    } catch (error) {
      console.error('Error fetching overtime:', error);
      if (error.response?.status !== 401) {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  }, [user, filterUser, filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id) {
      toast.error('Veuillez sélectionner un collaborateur');
      return;
    }
    if (!formData.hours || parseFloat(formData.hours) === 0) {
      toast.error('Veuillez entrer un nombre d\'heures valide');
      return;
    }
    if (!formData.reason || formData.reason.trim().length < 3) {
      toast.error('Veuillez entrer un motif (minimum 3 caractères)');
      return;
    }
    
    try {
      await axios.post(`${API}/api/overtime`, {
        user_id: formData.user_id,
        hours: parseFloat(formData.hours),
        reason: formData.reason.trim(),
        date: formData.date
      });
      
      toast.success(t('overtimeAdded'));
      setDialogOpen(false);
      setFormData({
        user_id: '',
        hours: '',
        reason: '',
        date: format(new Date(), 'yyyy-MM-dd')
      });
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      await axios.delete(`${API}/api/overtime/${entryToDelete.id}`);
      
      toast.success(t('overtimeDeleted'));
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Calculate totals
  const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const positiveHours = entries.filter(e => e.hours > 0).reduce((sum, e) => sum + e.hours, 0);
  const negativeHours = entries.filter(e => e.hours < 0).reduce((sum, e) => sum + e.hours, 0);

  // Clear filters
  const clearFilters = () => {
    setFilterUser('all');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('overtimeManagement')}</h1>
          <p className="text-muted-foreground text-sm">
            Gestion des heures supplémentaires des collaborateurs
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-overtime-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('addOvertime')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('totalOvertime')}</p>
                <p className={`text-2xl font-bold ${totalHours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalHours >= 0 ? '+' : ''}{totalHours.toFixed(1)}h
                </p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Heures ajoutées</p>
                <p className="text-2xl font-bold text-green-600">+{positiveHours.toFixed(1)}h</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Heures déduites</p>
                <p className="text-2xl font-bold text-red-600">{negativeHours.toFixed(1)}h</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtres
            </CardTitle>
            {(filterUser !== 'all' || filterStartDate || filterEndDate) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Effacer
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">{t('collaborator')} {team.length > 0 && `(${team.length})`}</Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger data-testid="filter-user">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent className="z-[200] max-h-[300px]">
                  <SelectItem value="all">Tous les collaborateurs</SelectItem>
                  {team.length === 0 ? (
                    <div className="px-2 py-1 text-sm text-muted-foreground">Chargement...</div>
                  ) : (
                    team.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date début</Label>
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">Date fin</Label>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des entrées</CardTitle>
          <CardDescription>{entries.length} entrée(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('noOvertimeEntries')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('date')}</TableHead>
                    <TableHead>{t('collaborator')}</TableHead>
                    <TableHead className="text-right">{t('overtimeHours')}</TableHead>
                    <TableHead>{t('overtimeReason')}</TableHead>
                    <TableHead>Créé par</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map(entry => (
                    <TableRow key={entry.id} data-testid={`overtime-entry-${entry.id}`}>
                      <TableCell className="font-medium">
                        {format(new Date(entry.date), 'dd MMM yyyy', { locale })}
                      </TableCell>
                      <TableCell>{entry.user_name}</TableCell>
                      <TableCell className={`text-right font-semibold ${entry.hours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {entry.hours >= 0 ? '+' : ''}{entry.hours.toFixed(1)}h
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={entry.reason}>
                        {entry.reason}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {entry.created_by_name}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setEntryToDelete(entry);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-overtime-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('addOvertime')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('collaborator')} *</Label>
              <Select 
                value={formData.user_id} 
                onValueChange={(v) => setFormData({...formData, user_id: v})}
              >
                <SelectTrigger data-testid="overtime-user-select">
                  <SelectValue placeholder="Sélectionner un collaborateur" />
                </SelectTrigger>
                <SelectContent className="z-[200] max-h-[300px]">
                  {team.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('date')} *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                data-testid="overtime-date-input"
              />
            </div>
            
            <div>
              <Label>{t('overtimeHours')} *</Label>
              <Input
                type="number"
                step="0.5"
                value={formData.hours}
                onChange={(e) => setFormData({...formData, hours: e.target.value})}
                placeholder="Ex: 2.5 ou -1.5"
                required
                data-testid="overtime-hours-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Positif pour ajouter, négatif pour déduire
              </p>
            </div>
            
            <div>
              <Label>{t('overtimeReason')} *</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                placeholder="Motif des heures supplémentaires..."
                rows={3}
                required
                data-testid="overtime-reason-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="overtime-submit-btn">
                Ajouter
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            {t('confirmDeleteOvertime')}
          </p>
          {entryToDelete && (
            <div className="bg-muted p-3 rounded-lg text-sm">
              <p><strong>{entryToDelete.user_name}</strong></p>
              <p>{format(new Date(entryToDelete.date), 'dd MMM yyyy', { locale })} - {entryToDelete.hours >= 0 ? '+' : ''}{entryToDelete.hours}h</p>
              <p className="text-muted-foreground">{entryToDelete.reason}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
